import { useState } from 'react'

import { useEffect } from 'modules/core/hooks/useEffect'

import documents from 'modules/documents/store/collections/documents'
import medicalLeaveUpdates from 'modules/timeoff/store/collections/medicalLeaveUpdates'
import Leave from 'modules/timeoff/store/models/Leave'

import usePermissions from './usePermissions'

interface Props {
  leaveId: number
  leaveEmployeeId: number
}

export type leaveConfirmationFields =
  | {
      leave_confirmation_id: string
      leave_confirmation_employee_id: string
      leave_confirmation_company_id: string
      remote_employee_id: string
      remote_request_id: string
      status: string
      is_initial_attest: boolean
      is_hospitalisation: boolean
      confirmed_start_date: Date
      confirmed_end_date: Date
      confirmed_diagnose_date: Date
      updated_at: Date
      created_at: Date
      error_messages: string[]
    }
  | Record<string, never>

const useLeaveData = ({ leaveId, leaveEmployeeId }: Props) => {
  const [leave, setLeave] = useState(new Leave(Leave.buildNullLeave(leaveId)))
  const [leaveConfirmationFields, setLeaveConfirmationFields] = useState({})
  const [loading, setLoading] = useState(true)
  const [isMedical, setIsMedical] = useState(false)
  const [medicalEffectiveOn, setMedicalEffectiveOn] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      await leave.fetch()
      setLeave(leave)
      setLeaveConfirmationFields(leave.attributes.leave_confirmation_fields)

      const promiseArray = [
        documents.fetch({
          data: {
            leave_id: leave.attributes.id,
          },
        }),
      ]

      // MEDICAL LEAVE DATA
      if (!medicalLeaveUpdates.length) {
        promiseArray.push(
          medicalLeaveUpdates.fetch({
            data: {
              employee_id: leaveEmployeeId,
            },
          })
        )
      }

      await Promise.all(promiseArray)

      const update = medicalLeaveUpdates.models.find(
        (u) => u.attributes.leave_id === leave.attributes.id && u.isAlta
      )

      setIsMedical(!!leave.attributes.medical_leave_id)
      setMedicalEffectiveOn(update?.attributes.effective_on)

      setLoading(false)
    }

    fetchData()
  }, [leaveId, leave.attributes.id])

  const permissions = usePermissions({
    leaveLoading: false,
    leave,
  })

  return {
    leave,
    loading,
    leaveConfirmationDetails: leaveConfirmationFields,
    medicalLeave: {
      isMedical,
      medicalEffectiveOn,
    },
    permissions,
  }
}

export default useLeaveData
